import type { AxiosInstance } from 'axios';

import { type ApiClientConfig, createApiClient } from './createApiClient';

/**
 * Lazy variant of `createApiClient` for module-scope clients.
 *
 * Building a client at module scope with a baseURL derived from
 * `config.exchangeApiUrl` dereferences the imported `config` binding — and,
 * inside `createApiClient`, the auth store — while the chunk may still be
 * evaluating mid-way through a circular import chain, where those bindings are
 * still `undefined`. The property read then throws during module instantiation
 * and takes down every module in the chunk (the production MobileSearchModal
 * crash; same class as #11465).
 *
 * This wrapper defers evaluating the client config and constructing the axios
 * instance to the first use, which is always after module instantiation has
 * finished. The returned proxy is a drop-in `AxiosInstance`: property reads,
 * writes, descriptor operations and direct calls all build the real client on
 * first touch and forward to it from then on, so call sites keep the
 * `api.get(...)` shape and `Object.defineProperty`-based overrides (such as
 * `vi.spyOn(api, 'get')`) land on the client that `get` reads from.
 *
 * The target is a configurable-only arrow function, so reporting the client's
 * (configurable) descriptors and keys stays within the Proxy invariants.
 */
export const createLazyApiClient = (getClientConfig: () => ApiClientConfig): AxiosInstance => {
    let client: AxiosInstance | undefined;
    const ensureClient = (): AxiosInstance => (client ??= createApiClient(getClientConfig()));

    return new Proxy((() => undefined) as unknown as AxiosInstance, {
        apply: (_target, thisArg, args) =>
            Reflect.apply(ensureClient() as unknown as (...callArgs: unknown[]) => unknown, thisArg, args),
        get: (_target, property) => Reflect.get(ensureClient(), property),
        set: (_target, property, value) => Reflect.set(ensureClient(), property, value),
        has: (_target, property) => Reflect.has(ensureClient(), property),
        defineProperty: (_target, property, descriptor) => Reflect.defineProperty(ensureClient(), property, descriptor),
        deleteProperty: (_target, property) => Reflect.deleteProperty(ensureClient(), property),
        getOwnPropertyDescriptor: (_target, property) => Reflect.getOwnPropertyDescriptor(ensureClient(), property),
        ownKeys: () => Reflect.ownKeys(ensureClient()),
        getPrototypeOf: () => Reflect.getPrototypeOf(ensureClient()),
    });
};
